import type { AgentCatalogSummary } from '../../lib/api'
import type { JsonObject, JsonValue } from './policyDocument'

interface PanelProps {
  parameters: JsonObject
  onChange: (parameters: JsonObject) => void
}

/**
 * The capability parameter forms.
 *
 * Every one of these edits `parameters` in place rather than rebuilding it from its fields. That is
 * the difference between this editor and the agent's own: the agent's guided form reconstructs the
 * parameter block from the controls it has, which silently deletes the `enforcement` block — the part
 * that actually stops a prompt being sent — from any policy that had one. A console that manages a
 * fleet cannot afford to quietly disarm a policy because someone opened it to fix a typo.
 */

export function LogPanel({ parameters, onChange }: PanelProps) {
  return (
    <div className="field">
      <label htmlFor="log-message">Message</label>
      <input
        id="log-message"
        value={text(parameters.message)}
        onChange={(event) => onChange({ ...parameters, message: event.target.value })}
        placeholder="Coastline is running."
        data-testid="parameter-message"
      />
      <div className="hint">Written to the device's own log. Nothing else reads it.</div>
    </div>
  )
}

export function WindowsStatePanel({ parameters, onChange }: PanelProps) {
  return (
    <div className="field">
      <label htmlFor="maximum-windows">Most windows to record</label>
      <input
        id="maximum-windows"
        type="number"
        min={1}
        max={5000}
        value={number(parameters.maximumWindows, 500)}
        onChange={(event) => onChange({ ...parameters, maximumWindows: clamp(event.target.value, 1, 5000, 500) })}
        data-testid="parameter-maximum-windows"
      />
      <div className="hint">
        A busy workstation has a few dozen. The ceiling exists so one machine with a runaway
        application cannot post a megabyte of window titles on every interval.
      </div>
    </div>
  )
}

const SCREENSHOT_TARGETS = [
  'The foreground window',
  'A window by name',
  'An application by name',
  'One display',
  'Every display'
]

export function ScreenshotPanel({ parameters, onChange }: PanelProps) {
  const target = number(parameters.target, 0)

  return (
    <>
      <div className="field">
        <label htmlFor="screenshot-target">What to capture</label>
        <select
          id="screenshot-target"
          value={target}
          onChange={(event) => {
            const next = Number(event.target.value)
            const updated: JsonObject = { ...parameters, target: next }
            // The fields the new target does not use are removed rather than left behind. A stale
            // `name` on a foreground capture is a question every future reader has to answer.
            if (next !== 1 && next !== 2) delete updated.name
            if (next !== 3) delete updated.displayIndex
            onChange(updated)
          }}
          data-testid="parameter-target"
        >
          {SCREENSHOT_TARGETS.map((label, index) => (
            <option key={label} value={index}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {(target === 1 || target === 2) && (
        <div className="field">
          <label htmlFor="screenshot-name">{target === 1 ? 'Window title contains' : 'Process name'}</label>
          <input
            id="screenshot-name"
            value={text(parameters.name)}
            onChange={(event) => onChange({ ...parameters, name: event.target.value })}
            placeholder={target === 1 ? 'Quarterly review' : 'EXCEL'}
            data-testid="parameter-name"
          />
        </div>
      )}

      {target === 3 && (
        <div className="field">
          <label htmlFor="display-index">Display</label>
          <input
            id="display-index"
            type="number"
            min={0}
            max={16}
            value={number(parameters.displayIndex, 0)}
            onChange={(event) => onChange({ ...parameters, displayIndex: clamp(event.target.value, 0, 16, 0) })}
            data-testid="parameter-display-index"
          />
          <div className="hint">Zero is the primary display.</div>
        </div>
      )}
    </>
  )
}

const SURFACES: [string, string][] = [
  ['desktop', 'A desktop application'],
  ['browser', 'A browser tab'],
  ['terminal', 'A terminal session']
]

const WINDOW_SCOPES: [string, string][] = [
  ['foreground', 'Only the window in front'],
  ['visible', 'Every visible window'],
  ['all', 'Every window, visible or not']
]

interface AgentPanelProps extends PanelProps {
  agents: AgentCatalogSummary[]
}

/**
 * The prompt-observation panel: which assistant, how its window is found, and what is read out of it.
 *
 * Choosing an assistant replaces the whole window-finding block from the catalog and keeps every key
 * the catalog does not define, so the detection rules and the enforcement block survive switching from
 * ChatGPT to Claude. Hand-editing the selectors is possible but folded away, because getting them
 * right is the catalog's job and an operator who opens that panel is usually there for one field.
 */
export function AgentPromptPanel({ parameters, agents, onChange }: AgentPanelProps) {
  const agentId = text(parameters.agentId)
  const matched = agents.find((agent) => agent.agentId === agentId)
  const surface = text(parameters.surface) || 'desktop'
  const selectors = object(parameters.promptSelectors)
  const ocr = object(parameters.ocr)
  const region = object(ocr.relativeRegion)
  const limits = object(parameters.limits)

  const chooseAgent = (key: string) => {
    const agent = agents.find((entry) => entry.key === key)
    if (!agent) return

    // The catalog block is the new targeting; everything it does not mention is the operator's and is
    // carried across. That is what makes `detection` and `enforcement` survive a change of assistant.
    const block = agent.parameters as JsonObject
    const next: JsonObject = { ...block }
    for (const [existing, value] of Object.entries(parameters)) if (!(existing in block)) next[existing] = value
    onChange(next)
  }

  return (
    <>
      <div className="field">
        <label htmlFor="agent-catalog">AI assistant</label>
        <select
          id="agent-catalog"
          value={matched?.key ?? ''}
          onChange={(event) => chooseAgent(event.target.value)}
          data-testid="parameter-agent"
        >
          {!matched && <option value="">Configured by hand ({agentId || 'no agent id'})</option>}
          {agents.map((agent) => (
            <option key={agent.key} value={agent.key}>
              {agent.displayName} — {agent.vendor}
            </option>
          ))}
        </select>
        <div className="hint">
          {matched
            ? matched.surfaceDescription
            : 'The process names and selectors below do not match any assistant in the catalog.'}
        </div>
      </div>

      <details className="subsection" data-testid="window-matching">
        <summary>How the window is found</summary>

        <div className="field">
          <label htmlFor="agent-surface">Surface</label>
          <select
            id="agent-surface"
            value={surface}
            onChange={(event) => onChange({ ...parameters, surface: event.target.value })}
          >
            {SURFACES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="window-scope">Which windows to look at</label>
          <select
            id="window-scope"
            value={text(parameters.windowScope) || 'visible'}
            onChange={(event) => onChange({ ...parameters, windowScope: event.target.value })}
          >
            {WINDOW_SCOPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <ListField
          id="process-names"
          label="Process names"
          value={parameters.processNames}
          onChange={(value) => onChange({ ...parameters, processNames: value })}
        />
        <ListField
          id="title-contains"
          label="Window title contains"
          value={parameters.documentTitleContains}
          onChange={(value) => onChange({ ...parameters, documentTitleContains: value })}
        />
        {surface === 'browser' && (
          <ListField
            id="browser-hosts"
            label="Browser hosts"
            hint="Only a tab on one of these hosts is read."
            value={parameters.browserHosts}
            onChange={(value) => onChange({ ...parameters, browserHosts: value })}
          />
        )}
        {surface === 'terminal' && (
          <>
            <ListField
              id="terminal-processes"
              label="Terminal processes"
              value={parameters.terminalProcessNames}
              onChange={(value) => onChange({ ...parameters, terminalProcessNames: value })}
            />
            <ListField
              id="terminal-markers"
              label="Text that identifies the session"
              value={parameters.terminalMarkers}
              onChange={(value) => onChange({ ...parameters, terminalMarkers: value })}
            />
            <ListField
              id="terminal-prefixes"
              label="Prompt prefixes"
              value={parameters.terminalPromptPrefixes}
              onChange={(value) => onChange({ ...parameters, terminalPromptPrefixes: value })}
            />
          </>
        )}
      </details>

      <details className="subsection" data-testid="prompt-selectors">
        <summary>Which control holds the prompt</summary>
        <ListField
          id="automation-ids"
          label="Automation ids"
          value={selectors.automationIds}
          onChange={(value) => onChange({ ...parameters, promptSelectors: { ...selectors, automationIds: value } })}
        />
        <ListField
          id="control-names"
          label="Control names"
          value={selectors.names}
          onChange={(value) => onChange({ ...parameters, promptSelectors: { ...selectors, names: value } })}
        />
        <ListField
          id="control-types"
          label="Control types"
          value={selectors.controlTypes}
          onChange={(value) => onChange({ ...parameters, promptSelectors: { ...selectors, controlTypes: value } })}
        />
      </details>

      <details className="subsection" data-testid="screen-text">
        <summary>Reading the screen, and the limits</summary>

        <label className="inline-check">
          <input
            type="checkbox"
            checked={ocr.enabled === true}
            onChange={(event) =>
              onChange({ ...parameters, ocr: { ...ocr, enabled: event.target.checked } })
            }
            data-testid="parameter-ocr"
          />
          <span>Read the screen when no control can be found</span>
        </label>
        <div className="hint">
          The fallback for an assistant that draws its own prompt box. It reads a rectangle of the
          window, so the region below is the part of the screen the prompt sits in.
        </div>

        {ocr.enabled === true && (
          <div className="number-grid">
            {(
              [
                ['left', 'Left', 0.12],
                ['top', 'Top', 0.65],
                ['width', 'Width', 0.76],
                ['height', 'Height', 0.28]
              ] as [string, string, number][]
            ).map(([key, label, fallback]) => (
              <div className="field" key={key}>
                <label htmlFor={`ocr-${key}`}>{label}</label>
                <input
                  id={`ocr-${key}`}
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={number(region[key], fallback)}
                  onChange={(event) =>
                    onChange({
                      ...parameters,
                      ocr: {
                        ...ocr,
                        relativeRegion: { ...region, [key]: clampFloat(event.target.value, 0, 1, fallback) }
                      }
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}

        <div className="number-grid">
          {(
            [
              ['maximumWindows', 'Windows', 50, 1, 50],
              ['maximumNodesPerWindow', 'Controls per window', 5000, 1, 5000],
              ['maximumPromptCharacters', 'Characters', 16384, 1, 16384],
              ['timeoutSecondsPerWindow', 'Seconds per window', 2, 0.1, 2]
            ] as [string, string, number, number, number][]
          ).map(([key, label, fallback, low, high]) => (
            <div className="field" key={key}>
              <label htmlFor={`limit-${key}`}>{label}</label>
              <input
                id={`limit-${key}`}
                type="number"
                min={low}
                max={high}
                step={key === 'timeoutSecondsPerWindow' ? 0.1 : 1}
                value={number(limits[key], fallback)}
                onChange={(event) =>
                  onChange({
                    ...parameters,
                    limits: { ...limits, [key]: clampFloat(event.target.value, low, high, fallback) }
                  })
                }
              />
            </div>
          ))}
        </div>
        <div className="hint">
          These are the agent's own ceilings. They bound how long one pass over the windows can take,
          which is what keeps a policy on a fifteen-second interval from becoming the reason a machine
          feels slow.
        </div>
      </details>
    </>
  )
}

function ListField({
  id,
  label,
  hint,
  value,
  onChange
}: {
  id: string
  label: string
  hint?: string
  value: JsonValue | undefined
  onChange: (value: JsonValue) => void
}) {
  const items = Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={items.join(', ')}
        onChange={(event) =>
          onChange(
            event.target.value
              .split(',')
              .map((item) => item.trim())
              .filter((item) => item.length > 0)
          )
        }
        data-testid={id}
      />
      <div className="hint">{hint ?? 'Separated by commas.'}</div>
    </div>
  )
}

function text(value: JsonValue | undefined): string {
  return typeof value === 'string' ? value : ''
}

function number(value: JsonValue | undefined, fallback: number): number {
  return typeof value === 'number' ? value : fallback
}

function object(value: JsonValue | undefined): JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as JsonObject) : {}
}

function clamp(raw: string, low: number, high: number, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? fallback : Math.min(high, Math.max(low, parsed))
}

function clampFloat(raw: string, low: number, high: number, fallback: number): number {
  const parsed = Number.parseFloat(raw)
  return Number.isNaN(parsed) ? fallback : Math.min(high, Math.max(low, parsed))
}
